import * as React from 'react';
import { useSelector } from 'react-redux';
import GlobalStyles from '@mui/material/GlobalStyles';
import { TranslatePlain } from '../../../shared/LanguageService';
import SuccessSnackbar from '../../../shared/feedback/SuccessSnackbar';
import { getUser } from '../../../reducers/userReducer';
import { canEditTranslations } from '../state/canEditTranslations';
import { editorHighlightStyles } from '../state/highlightTranslatedText';
import { useTranslationEditor } from '../state/TranslationEditorContext';
import { useEditTranslationsShortcut } from '../state/useEditTranslationsShortcut';
import { useTranslationKeyClicks } from '../state/useTranslationKeyClicks';
import { useTranslationKeyHover } from '../state/useTranslationKeyHover';
import TranslationEditorDrawer from './TranslationEditorDrawer';
import TranslationKeyBadge from './TranslationKeyBadge';
import NotificationPreviews from './NotificationPreviews';

type TranslationEditorScopeProps = {
    children: React.ReactNode;
};

function TranslationEditorScope({ children }: TranslationEditorScopeProps) {
    const { editTranslationsEnabled, showNotifications, savedTranslation, dismissSavedNotice } =
        useTranslationEditor();
    const user = useSelector(getUser);
    useEditTranslationsShortcut(canEditTranslations(user));
    const { handleClickCapture } = useTranslationKeyClicks();
    const { hovered, handleMouseOverCapture, handleMouseLeave } = useTranslationKeyHover();

    return (
        <>
            <GlobalStyles styles={editorHighlightStyles} />
            <div
                onClickCapture={editTranslationsEnabled ? handleClickCapture : undefined}
                onMouseOverCapture={editTranslationsEnabled ? handleMouseOverCapture : undefined}
                onMouseLeave={editTranslationsEnabled ? handleMouseLeave : undefined}
            >
                {children}
                {editTranslationsEnabled && showNotifications ? <NotificationPreviews /> : null}
            </div>
            {editTranslationsEnabled && hovered !== null ? (
                <TranslationKeyBadge hovered={hovered} />
            ) : null}
            {editTranslationsEnabled ? <TranslationEditorDrawer /> : null}
            <SuccessSnackbar
                open={savedTranslation !== null}
                onClose={dismissSavedNotice}
                text={TranslatePlain('translation-editor/snackbar/saved')}
            />
        </>
    );
}

export default TranslationEditorScope;
